import { useCallback, useMemo } from 'react';

import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import {
	useSupporterVoicesVideoFirstTrackingProps,
	useVideoFirstAskArm,
	useVideoFirstFlow,
} from '#src/app/pages/actions/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

type Result = ModelHookResult<
	{
		skipLink: string;
		shouldRender: boolean;
	},
	{
		onSkipLinkClick: () => void;
	}
>;

export function useSkipLink(): Result {
	const {
		data: { petition },
	} = usePetitionActionsPageData();
	const [{ shouldDisplaySupporterVoicesVideo, shareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	const {
		data: { hasRecordedVideo },
	} = useVideoFirstFlow();
	const videoFirstAskArm = useVideoFirstAskArm();
	const { track } = useCampaignTracking();
	const trackingProps = useSupporterVoicesVideoFirstTrackingProps();

	const skipLink = useMemo(() => {
		return `/p/${petition.slug}/d`;
	}, [petition.slug]);

	/**
	 * This link sits below the action card on every step, and leaves the flow for the recommendations
	 * page. On the arms that carry on to the comment, two of those steps now offer their own way out -
	 * a "Skip" that goes to the comment rather than out of the flow - and rendering both would put two
	 * controls reading "Skip" next to each other pointing at different places.
	 *
	 * So it stands down wherever a screen owns its own exit, matching the prototype, which shows
	 * exactly one such control per screen:
	 *
	 *   - the video steps (ask, recorder, preview) on those arms, where the in-card Skip goes to the
	 *     comment;
	 *   - the post-comment screen once a video exists, where the terminal "View petition" is the way
	 *     onward and there is nothing left to skip past.
	 *
	 * It still renders everywhere else, including the whole of control's flow and the comment step,
	 * where leaving for the recommendations is exactly what the prototype's own Skip does.
	 */
	const shouldRender = useMemo(() => {
		if (hasRecordedVideo && shareSupporterVoiceSuccess) {
			return false;
		}

		return !(videoFirstAskArm !== undefined && shouldDisplaySupporterVoicesVideo && !shareSupporterVoiceSuccess);
	}, [hasRecordedVideo, videoFirstAskArm, shareSupporterVoiceSuccess, shouldDisplaySupporterVoicesVideo]);

	const onSkipLinkClick = useCallback(() => {
		track('campaign_action_skip_click', trackingProps);
	}, [track, trackingProps]);

	return { data: { skipLink, shouldRender }, actions: { onSkipLinkClick } };
}
